import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { SPORTS, CITIES, SKILL_LEVELS } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import PageShell from "../components/PageShell";
import BottomNav from "../components/BottomNav";
import PaymentSetupSection from "../components/PaymentSetupSection";
import { ArrowLeft, ChevronDown, Trophy } from "lucide-react";

export default function CreateMatch() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const preselectedTeam = searchParams.get("team_id") || "";
  const [form, setForm] = useState({
    sport: "futsal",
    venue: "",
    city: user?.city || "Jakarta",
    starts_date: "",
    starts_time: "",
    duration_minutes: 90,
    max_players: 10,
    skill_level: "all",
    total_cost: 0,
    cost_per_player: 0,
    payment_method: "transfer",
    payment_notes: "",
    qris_image: null,
    payment_deadline_date: "",
    payment_deadline_time: "",
    description: "",
    whatsapp_link: "",
    team_id: preselectedTeam,
  });
  const [submitting, setSubmitting] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [myTeams, setMyTeams] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/teams", { params: { mine: true } });
        setMyTeams(data);
      } catch (_) {}
    })();
  }, []);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  // Auto-derive per player when total or max changes
  const handleTotal = (v) => {
    const total = Number(v) || 0;
    const max = Number(form.max_players) || 0;
    update("total_cost", total);
    if (max > 0) update("cost_per_player", Math.floor(total / max));
  };
  const handlePerPlayer = (v) => {
    const cpp = Number(v) || 0;
    const max = Number(form.max_players) || 0;
    update("cost_per_player", cpp);
    if (max > 0) update("total_cost", cpp * max);
  };

  const submit = async () => {
    if (!form.venue || !form.starts_date || !form.starts_time) {
      toast.error("Lengkapi venue dan waktu");
      return;
    }
    const starts_at = new Date(`${form.starts_date}T${form.starts_time}:00`).toISOString();
    const payment_deadline = (form.payment_deadline_date && form.payment_deadline_time)
      ? new Date(`${form.payment_deadline_date}T${form.payment_deadline_time}:00`).toISOString()
      : null;
    setSubmitting(true);
    try {
      const { data } = await api.post("/matches", {
        sport: form.sport,
        venue: form.venue,
        city: form.city,
        starts_at,
        duration_minutes: Number(form.duration_minutes),
        max_players: Number(form.max_players),
        skill_level: form.skill_level,
        total_cost: Number(form.total_cost) || 0,
        cost_per_player: Number(form.cost_per_player) || 0,
        payment_method: form.payment_method,
        payment_notes: form.payment_notes || null,
        qris_image: form.qris_image || null,
        payment_deadline,
        description: form.description,
        whatsapp_link: form.whatsapp_link || null,
      });
      toast.success("Match dibuat! 🎉");
      navigate(`/match/${data.match_id}`, { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal buat match");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageShell>
      <header className="px-4 pt-6 pb-2 flex items-center gap-3">
        <button onClick={() => navigate(-1)} data-testid="back-btn" className="h-10 w-10 rounded-full bg-secondary border border-border flex items-center justify-center">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <div className="text-xs text-muted-foreground font-bold uppercase tracking-wider">Buat Match</div>
          <h1 className="font-heading text-2xl font-black tracking-tight">Atur Match Lo</h1>
        </div>
      </header>

      <div className="px-4 mt-4 space-y-5">
        <Field label="Olahraga">
          <div className="grid grid-cols-4 gap-2">
            {SPORTS.map((s) => (
              <button
                key={s.id}
                data-testid={`create-sport-${s.id}`}
                onClick={() => update("sport", s.id)}
                className={`flex flex-col items-center gap-1 py-3 rounded-2xl border transition-all ${form.sport === s.id ? "border-primary bg-primary/10" : "border-border bg-secondary"}`}
              >
                <span className="text-xl">{s.icon}</span>
                <span className="text-[10px] font-bold">{s.label}</span>
              </button>
            ))}
          </div>
        </Field>

        <Field label="Venue / Lokasi">
          <input
            data-testid="create-venue"
            value={form.venue}
            onChange={(e) => update("venue", e.target.value)}
            placeholder="contoh: GOR Senayan Lapangan 3"
            className="w-full bg-secondary border border-border rounded-2xl px-4 py-3.5 text-foreground placeholder-muted-foreground/60 focus:outline-none focus:border-primary"
          />
        </Field>

        <Field label="Kota">
          <select
            data-testid="create-city"
            value={form.city}
            onChange={(e) => update("city", e.target.value)}
            className="w-full bg-secondary border border-border rounded-2xl px-4 py-3.5 text-foreground focus:outline-none focus:border-primary"
          >
            {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Tanggal">
            <input data-testid="create-date" type="date" value={form.starts_date} onChange={(e) => update("starts_date", e.target.value)} className="w-full bg-secondary border border-border rounded-2xl px-4 py-3.5 focus:outline-none focus:border-primary" />
          </Field>
          <Field label="Jam">
            <input data-testid="create-time" type="time" value={form.starts_time} onChange={(e) => update("starts_time", e.target.value)} className="w-full bg-secondary border border-border rounded-2xl px-4 py-3.5 focus:outline-none focus:border-primary" />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Maks Pemain">
            <input data-testid="create-max" type="number" min={2} max={30} value={form.max_players} onChange={(e) => {
              const v = e.target.value;
              update("max_players", v);
              // recompute derivations
              const max = Number(v) || 0;
              if (form.total_cost && max > 0) update("cost_per_player", Math.floor(form.total_cost / max));
              else if (form.cost_per_player && max > 0) update("total_cost", form.cost_per_player * max);
            }} className="w-full bg-secondary border border-border rounded-2xl px-4 py-3.5 focus:outline-none focus:border-primary" />
          </Field>
          <Field label="Durasi (menit)">
            <input data-testid="create-duration" type="number" min={30} step={15} value={form.duration_minutes} onChange={(e) => update("duration_minutes", e.target.value)} className="w-full bg-secondary border border-border rounded-2xl px-4 py-3.5 focus:outline-none focus:border-primary" />
          </Field>
        </div>

        <Field label="Skill Level">
          <div className="grid grid-cols-2 gap-2">
            {SKILL_LEVELS.map((l) => (
              <button key={l.id} data-testid={`create-skill-${l.id}`} onClick={() => update("skill_level", l.id)}
                className={`px-3 py-2.5 rounded-full text-sm font-bold transition-all ${form.skill_level === l.id ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground border border-border"}`}>
                {l.label}
              </button>
            ))}
          </div>
        </Field>

        {/* Team selector */}
        {myTeams.length > 0 && (
          <Field label="Host atas nama Team (optional)">
            <div className="space-y-2">
              <button
                onClick={() => update("team_id", "")}
                data-testid="team-none"
                className={`w-full text-left px-4 py-3 rounded-2xl border ${!form.team_id ? "border-primary bg-primary/10" : "border-border bg-secondary"} text-sm font-bold`}
              >
                Tanpa Team (personal)
              </button>
              {myTeams.map((t) => (
                <button
                  key={t.team_id}
                  data-testid={`team-pick-${t.team_id}`}
                  onClick={() => update("team_id", t.team_id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-2xl border ${form.team_id === t.team_id ? "border-primary bg-primary/10" : "border-border bg-secondary"} text-left`}
                >
                  <div className="h-9 w-9 rounded-xl overflow-hidden bg-card flex items-center justify-center shrink-0">
                    {t.logo ? <img src={t.logo} alt="" className="w-full h-full object-cover" /> : <Trophy className="h-4 w-4 text-primary" />}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-bold truncate">{t.name}</div>
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{t.sport} · {t.city}</div>
                  </div>
                </button>
              ))}
            </div>
          </Field>
        )}

        {/* Payment section toggle */}
        <button
          onClick={() => setShowPayment((v) => !v)}
          data-testid="toggle-payment"
          className="w-full bg-card border border-border rounded-2xl px-4 py-3.5 flex items-center justify-between hover:border-primary/40"
        >
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">💸</div>
            <div className="text-left">
              <div className="font-bold text-sm">Payment Setup</div>
              <div className="text-xs text-muted-foreground">Total, metode, bukti — koordinasi bayar match</div>
            </div>
          </div>
          <ChevronDown className={`h-5 w-5 transition-transform ${showPayment ? "rotate-180" : ""}`} />
        </button>

        {showPayment && (
          <PaymentSetupSection
            form={form}
            update={update}
            handleTotal={handleTotal}
            handlePerPlayer={handlePerPlayer}
          />
        )}

        <Field label="Deskripsi (optional)">
          <textarea data-testid="create-desc" rows={3} value={form.description} onChange={(e) => update("description", e.target.value)} placeholder="Bawa sepatu, parkir di depan, dll." className="w-full bg-secondary border border-border rounded-2xl px-4 py-3 focus:outline-none focus:border-primary resize-none" />
        </Field>

        <Field label="Link WhatsApp Grup (optional)">
          <input data-testid="create-wa" value={form.whatsapp_link} onChange={(e) => update("whatsapp_link", e.target.value)} placeholder="https://chat.whatsapp.com/..." className="w-full bg-secondary border border-border rounded-2xl px-4 py-3.5 focus:outline-none focus:border-primary" />
        </Field>

        <button
          data-testid="create-submit"
          onClick={submit}
          disabled={submitting}
          className="w-full px-6 py-4 rounded-full bg-primary text-primary-foreground font-bold glow-cyan-strong active:scale-95 transition-transform disabled:opacity-50"
        >
          {submitting ? "Membuat..." : "Buat Match 🚀"}
        </button>
      </div>

      <BottomNav />
    </PageShell>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{label}</label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
